import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, Link } from 'react-router-dom';
import { authStore } from '@/stores/authStore';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useTranslation } from 'react-i18next';
import { LogOut, Settings, User, HelpCircle } from 'lucide-react';

interface UserMenuProps {
  isMobile?: boolean;
  onMenuItemClick?: () => void;
}

const UserMenu = observer(({ isMobile = false, onMenuItemClick }: UserMenuProps) => {
  const navigate = useNavigate();
  const user = authStore.user;
  const { t } = useTranslation();

  // Helper function to handle logout
  const handleLogout = () => {
    authStore.logout();
    navigate('/login');
    // Call the callback if provided
    if (onMenuItemClick) {
      onMenuItemClick();
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  // If in mobile view, render a simpler menu without the dropdown
  if (isMobile) {
    return (
      <div className="space-y-2 w-full">
        <Link 
          to="/account" 
          className="flex items-center space-x-2 text-gray-700 hover:bg-gray-100 px-2 py-1.5 rounded-md w-full"
          onClick={onMenuItemClick}
        >
          <User size={16} />
          <span>{t('navigation.account')}</span>
        </Link>
        <Link 
          to="/security" 
          className="flex items-center space-x-2 text-gray-700 hover:bg-gray-100 px-2 py-1.5 rounded-md w-full"
          onClick={onMenuItemClick}
        >
          <Settings size={16} />
          <span>{t('navigation.security')}</span>
        </Link>
        <Link 
          to="/help" 
          className="flex items-center space-x-2 text-gray-700 hover:bg-gray-100 px-2 py-1.5 rounded-md w-full"
          onClick={onMenuItemClick}
        >
          <HelpCircle size={16} />
          <span>{t('navigation.help')}</span>
        </Link>
        <button 
          type="button"
          onClick={handleLogout}
          className="flex items-center space-x-2 text-red-600 hover:bg-red-50 px-2 py-1.5 rounded-md w-full"
        >
          <LogOut size={16} />
          <span>{t('navigation.logout')}</span>
        </button>
      </div>
    );
  }

  // Desktop dropdown menu view
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarFallback>{user?.name ? getInitials(user.name) : '?'}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link to="/account">{t('navigation.account')}</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/security">{t('navigation.security')}</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/help">{t('navigation.help')}</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout}>
          {t('navigation.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

export default UserMenu; 